/**
 * Greetings for the phone system: whatever someone uploads or records becomes an 8 kHz, mono,
 * 16-bit WAV here in the browser (what the phone system plays), with silence trimmed and a
 * quiet recording brought up to a comfortable level. Nothing leaves the browser until it is
 * converted.
 */

export const MAX_GREETING_SECONDS = 180;
const RATE = 8000;

export class AudioProblem extends Error {}

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

/** Decode any audio the browser can play (mp3, m4a, wav, webm...) and convert it. */
export async function toPhoneWav(input: Blob): Promise<{ wav: Blob; seconds: number }> {
  const Ctx = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
  if (!Ctx || typeof OfflineAudioContext === 'undefined') throw new AudioProblem('This browser can’t convert audio. Try a current version of Chrome, Edge, Safari or Firefox.');
  const ctx = new Ctx();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(await input.arrayBuffer());
  } catch {
    throw new AudioProblem('That file isn’t audio this browser can read. Try an MP3, M4A or WAV file.');
  } finally {
    void ctx.close();
  }
  if (decoded.duration > MAX_GREETING_SECONDS + 30) throw new AudioProblem(`Keep greetings under ${MAX_GREETING_SECONDS / 60} minutes.`);

  // One channel at 8 kHz: the browser mixes stereo down and resamples (with filtering).
  const frames = Math.max(1, Math.ceil(decoded.duration * RATE));
  const offline = new OfflineAudioContext(1, frames, RATE);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const rendered = await offline.startRendering();
  const samples = trimSilence(rendered.getChannelData(0));
  if (samples.length < RATE * 0.3) throw new AudioProblem('That recording is silent or too short.');
  if (samples.length > RATE * MAX_GREETING_SECONDS) throw new AudioProblem(`Keep greetings under ${MAX_GREETING_SECONDS / 60} minutes.`);
  const levelled = level(samples);
  return { wav: encodeWav(levelled), seconds: Math.round((levelled.length / RATE) * 10) / 10 };
}

/** Drop silence at the start and end, keeping a short lead-in and tail. */
function trimSilence(data: Float32Array): Float32Array {
  const threshold = 0.01;
  let start = 0;
  while (start < data.length && Math.abs(data[start]!) < threshold) start++;
  let end = data.length - 1;
  while (end > start && Math.abs(data[end]!) < threshold) end--;
  if (start >= end) return new Float32Array(0);
  const pad = Math.round(RATE * 0.25);
  return data.slice(Math.max(0, start - pad), Math.min(data.length, end + pad));
}

/** Bring a quiet recording up so its loudest moment sits just below full scale. Never louder than 4x. */
function level(data: Float32Array): Float32Array {
  let peak = 0;
  for (const v of data) peak = Math.max(peak, Math.abs(v));
  if (peak === 0 || peak > 0.7) return data;
  const gain = Math.min(4, 0.89 / peak);
  return data.map((v) => v * gain);
}

function encodeWav(data: Float32Array): Blob {
  const buf = new ArrayBuffer(44 + data.length * 2);
  const v = new DataView(buf);
  const text = (at: number, s: string) => [...s].forEach((c, i) => v.setUint8(at + i, c.charCodeAt(0)));
  text(0, 'RIFF');
  v.setUint32(4, 36 + data.length * 2, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, RATE, true);
  v.setUint32(28, RATE * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  text(36, 'data');
  v.setUint32(40, data.length * 2, true);
  data.forEach((s, i) => v.setInt16(44 + i * 2, Math.round(Math.max(-1, Math.min(1, s)) * 32767), true));
  return new Blob([buf], { type: 'audio/wav' });
}

export interface Recorder {
  /** Stop and get what was recorded. */
  stop(): Promise<Blob>;
  /** Stop without keeping anything. */
  cancel(): void;
  /** 0..1, for a level meter. */
  level(): number;
}

/** Record from the microphone. Asks the browser for permission first. */
export async function startRecording(): Promise<Recorder> {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    throw new AudioProblem('This browser can’t record. Upload a file instead.');
  }
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  } catch {
    throw new AudioProblem('Stitch couldn’t use your microphone. Allow microphone access in your browser, then try again.');
  }
  const recorder = new MediaRecorder(stream);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  const Ctx = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
  const meterCtx = Ctx ? new Ctx() : null;
  const analyser = meterCtx?.createAnalyser();
  if (meterCtx && analyser) meterCtx.createMediaStreamSource(stream).connect(analyser);
  const buf = new Float32Array(analyser?.fftSize ?? 0);
  const release = () => {
    stream.getTracks().forEach((t) => t.stop());
    void meterCtx?.close();
  };
  recorder.start(250);
  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        recorder.onstop = () => {
          release();
          resolve(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
        };
        recorder.stop();
      }),
    cancel: () => {
      recorder.onstop = release;
      if (recorder.state !== 'inactive') recorder.stop();
      else release();
    },
    level: () => {
      if (!analyser) return 0;
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += v * v;
      return Math.min(1, Math.sqrt(sum / buf.length) * 4);
    }
  };
}

/** "0:12", "1:05". */
export function clockTime(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
